/**
 * THE ONE OWNING MODULE for every impersonation ("view as") read and for the
 * self-exit (SPEC-FRONTEND-RULE-077, RULE-095(11), SPEC-FRONTEND-API-013).
 *
 * It owns, and nothing else in `web/src` may re-declare:
 * - the cache key and the single fetcher for `GET /api/v1/me/impersonation`
 *   (server contract SPEC-USER-AUTH-API-007), the caller's own act-as
 *   descriptor. An EMPTY object means "no live binding" and is a legitimate
 *   answer, never an error state;
 * - the self-exit `DELETE /api/v1/impersonation/sessions/{ref}`
 *   (SPEC-GO-API-CONTRACT-039). 204 and 200 `{already_ended: true}` are BOTH
 *   success, and 404 is "not mine, already gone";
 * - the act-as GETTER (RULE-095(6)(B)) the four stamping lanes read. It is a
 *   getter that FEEDS the lanes, not a lane itself;
 * - the CLOSED enum of terminal refusal codes that `api/client.ts`'s terminal
 *   branch matches, and their mapping onto the banner's end codes, so the
 *   interceptor and the banner's terminal card can never drift.
 *
 * THE TERMINAL ENUM FOLLOWS THE WIRE, NOT THE TASK TEXT. The act-as middleware
 * (`internal/api/middleware/actas.go`, `refuse`) answers every refusal as a 403
 * whose body is `{error, code, message, details: {reason}}` - the row's
 * `end_reason` goes to the durable audit row only and never reaches the
 * browser. So the codes matched here are the middleware's `ActAsReason*`
 * spellings, and the banner's end code is derived from them where the reason
 * pins one (a re-authentication, the kill switch) and left undefined - the
 * card's generic "The view has ended." - where it does not.
 *
 * NOTHING HERE IS PERSISTED. The descriptor carries no credential because none
 * exists; the binding reference is non-secret and inert for anyone but the
 * bound impersonator (SPEC-USER-AUTH-RULE-020(1)). The only browser storage
 * this feature touches is the one-bootstrap `sessionStorage` hint of
 * `utils/impersonationHint.ts`.
 *
 * @module api/impersonation
 * @see TASK-IMP-AUTHCONTEXT-SPA-WIRING-CHG-001
 * @see SPEC-FRONTEND-RULE-095
 */
import { useSyncExternalStore } from "react";
import type { AxiosError } from "axios";
import client from "./client";
import type {
  ImpersonationBinding,
  ImpersonationEndCode,
} from "../components/shared/ImpersonationBanner";

// ---------------------------------------------------------------------------
// Wire shapes (internal/api/impersonation/dto.go)
// ---------------------------------------------------------------------------

/** One person in a response: the identity-provider subject and a display name. */
export interface ImpersonationPersonRef {
  user_id: string;
  /** Omitted on the descriptor (its performance contract allows no extra I/O). */
  display_name?: string;
}

/** The target block of SPEC-USER-AUTH-API-007 (dto.go `TargetBlock`). */
export interface ImpersonationTargetBlock {
  user_id: string;
  /** Omitted on the descriptor; carried by the start's 201 only. */
  display_name?: string;
  tenant_id: string;
  /** Omitted on the descriptor; carried by the start's 201 only. */
  tenant_name?: string;
  highest_role: string;
  /** "attribute" or "membership" (SPEC-USER-AUTH-RULE-019(3)). */
  tenant_source: string;
}

/**
 * The non-empty 200 body of `GET /api/v1/me/impersonation`
 * (dto.go `DescriptorResponse`).
 */
export interface ImpersonationDescriptor {
  /** The NON-SECRET binding reference. Safe to show and to log. */
  session_ref: string;
  mode: "view" | "act";
  /** 1 for a direct binding, 2 for a nested one. */
  depth: number;
  via?: ImpersonationPersonRef;
  parent_session_ref?: string;
  target: ImpersonationTargetBlock;
  started_at: string;
  /** The binding row's own ABSOLUTE deadline - the banner's only countdown input. */
  expires_at: string;
  target_surface: "tenant" | "workspace";
  /** Where the exit and the terminal card send the operator back to. */
  return_to: string;
  /** Whether the latest server-side role re-check succeeded inside its window. */
  roles_recheck_ok: boolean;
}

/** The 200 body of the self-exit when the binding had already ended. */
interface ExitResponse {
  already_ended?: boolean;
}

// ---------------------------------------------------------------------------
// The descriptor read - ONE key, ONE fetcher
// ---------------------------------------------------------------------------

/**
 * THE cache key for the caller's own act-as descriptor. Any surface that reads
 * the descriptor through TanStack Query uses this key WITH
 * {@link fetchImpersonationDescriptor}; a second queryFn on it fails
 * QUERY-KEY-GATE.
 */
export const IMPERSONATION_DESCRIPTOR_QUERY_KEY = ["me", "impersonation"] as const;

/** The descriptor route, relative to the api client's base URL. */
const DESCRIPTOR_PATH = "/me/impersonation";

/**
 * Read the caller's own act-as descriptor.
 *
 * Resolves `null` for the EMPTY object - "no live binding", a legitimate
 * answer and never an error. Any failure rejects, so the shell renders a LOUD
 * error rather than an absent banner (RULE-042).
 */
export async function fetchImpersonationDescriptor(): Promise<ImpersonationDescriptor | null> {
  const res = await client.get<Partial<ImpersonationDescriptor>>(DESCRIPTOR_PATH);
  const body = res.data;
  if (!body || typeof body !== "object" || !body.session_ref) {
    return null;
  }
  return body as ImpersonationDescriptor;
}

/**
 * The query options every TanStack reader spreads, so the key and the fetcher
 * travel together and cannot be paired wrongly.
 */
export const impersonationDescriptorQuery = {
  queryKey: IMPERSONATION_DESCRIPTOR_QUERY_KEY,
  queryFn: fetchImpersonationDescriptor,
};

// ---------------------------------------------------------------------------
// The self-exit
// ---------------------------------------------------------------------------

/** How a self-exit call ended. Every value is a success for the SPA. */
export type ImpersonationExitOutcome = "ended" | "already_ended" | "not_found";

export interface ExitImpersonationOptions {
  /**
   * The call is issued from a TEARDOWN path (logout / forceLogout), where the
   * access token may already be dead. The interceptor then neither refreshes
   * nor retries it, so a teardown can never re-enter the refresh backoff loop.
   */
  teardown?: boolean;
}

/**
 * How long a TEARDOWN-path exit may hold up a logout, in ms. DERIVATION: the
 * exit is one indexed update plus an audit write; the same 3-second budget the
 * fire-and-forget debug lane uses (api/client.ts `debugClient`) would starve a
 * slow audit write, so the budget is 5 seconds. It bounds only how long the
 * end-session navigation waits; the server-side ends (idle, expiry, the
 * re-authentication void) end the binding regardless. revisit: if the exit's
 * measured p99 approaches it.
 */
const TEARDOWN_EXIT_TIMEOUT_MS = 5_000;

/**
 * End the caller's OWN binding (SPEC-GO-API-CONTRACT-039).
 *
 * The request carries the per-request `skipActAs` flag, so the interceptor
 * omits `X-Verisa-Act-As` - the client-side CONVENIENCE; the server carve-out
 * resolved against the pre-substitution identity is the guarantee. 204 and
 * 200 `{already_ended: true}` both resolve as success; 404 resolves as
 * `not_found` ("not mine, already gone"). Anything else rejects, and the shell
 * then shows the persistent "the view will end at {time}" notice rather than a
 * pretended success.
 */
export async function exitImpersonationSession(
  sessionRef: string,
  options: ExitImpersonationOptions = {},
): Promise<ImpersonationExitOutcome> {
  try {
    const res = await client.delete<ExitResponse | "">(
      `/impersonation/sessions/${encodeURIComponent(sessionRef)}`,
      options.teardown
        ? { skipActAs: true, skipAuthRefresh: true, timeout: TEARDOWN_EXIT_TIMEOUT_MS }
        : { skipActAs: true },
    );
    if (res.status === 200 && typeof res.data === "object" && res.data?.already_ended) {
      return "already_ended";
    }
    return "ended";
  } catch (err) {
    if ((err as AxiosError | undefined)?.response?.status === 404) {
      return "not_found";
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// The act-as getter (RULE-095(6)(B)) - feeds the four stamping lanes
// ---------------------------------------------------------------------------

/** The HTTP header every stamping lane writes. Mirrors middleware.HeaderActAs. */
export const ACT_AS_HEADER = "X-Verisa-Act-As";

let _getActAsSessionRef: () => string | null = () => null;

/** Register the act-as getter (called by AuthContext when a binding arms). */
export function setActAsGetter(fn: () => string | null): void {
  _getActAsSessionRef = fn;
}

/**
 * The live binding's reference, or null when no binding is live. A SYNCHRONOUS
 * read, so the stamp adds no request.
 */
export function getActAsSessionRef(): string | null {
  return _getActAsSessionRef();
}

/**
 * The act-as header for a raw `fetch` lane, or an empty object when no binding
 * is live. Spread it beside `Authorization`.
 */
export function actAsHeaders(): Record<string, string> {
  const ref = getActAsSessionRef();
  return ref ? { [ACT_AS_HEADER]: ref } : {};
}

// ---------------------------------------------------------------------------
// The binding-discovery signal (TASK-IMP-BRANDING-UNDER-BINDING CHG-005)
// ---------------------------------------------------------------------------

/**
 * Where the impersonation discovery (the arrival read of RULE-095(5)) stands,
 * for the raw-fetch lanes that live outside React context:
 * - `pending`: not yet answered - a read made now may leave BEFORE the act-as
 *   getter is armed and be answered for the operator;
 * - `settled`: answered - no binding, or a live binding whose getter is armed,
 *   or discovery is not active in this app;
 * - `error`: the arrival read failed (its loud error state); the identity a read
 *   would carry is unknown.
 *
 * Module-level for the same reason the act-as getter is. AuthContext is its one
 * writer; it starts `pending`.
 */
export type ImpersonationDiscoveryState = "pending" | "settled" | "error";

let _discoveryState: ImpersonationDiscoveryState = "pending";
const _discoveryListeners = new Set<() => void>();

/** The current discovery state - a synchronous read. */
export function getImpersonationDiscoveryState(): ImpersonationDiscoveryState {
  return _discoveryState;
}

/** Publish the discovery state (AuthContext). Listeners run on a change only. */
export function setImpersonationDiscoveryState(next: ImpersonationDiscoveryState): void {
  if (next === _discoveryState) return;
  _discoveryState = next;
  for (const fn of Array.from(_discoveryListeners)) fn();
}

/** Subscribe to discovery-state changes; returns the unsubscribe function. */
export function subscribeImpersonationDiscovery(fn: () => void): () => void {
  _discoveryListeners.add(fn);
  return () => {
    _discoveryListeners.delete(fn);
  };
}

// ---------------------------------------------------------------------------
// The CLOSED terminal enum
// ---------------------------------------------------------------------------

/**
 * Every refusal that means THE BINDING IS OVER (or can never be used), spelled
 * exactly as `internal/api/middleware/actas.go` spells it. Closed: a code not
 * listed here is an ordinary error, never a terminal one.
 *
 * DELIBERATELY ABSENT, and why:
 * - `impersonation_settings_unreadable`, `impersonation_binding_store_unreadable`:
 *   fail-closed OUTAGES. The binding may well still be live, so ending the view
 *   on them would be a pretended end.
 * - `impersonation_action_refused`, `impersonation_refused_bypass_posture`: the
 *   view-mode refusal layer refusing ONE request; the binding stays live.
 */
export const IMPERSONATION_TERMINAL_REASONS = [
  "impersonation_binding_ended",
  "impersonation_lifecycle_ended",
  "impersonation_reauth",
  "act_as_binding_mismatch",
  "impersonation_disabled",
  "impersonation_refused_sovereign_mode",
] as const;

export type ImpersonationTerminalReason = (typeof IMPERSONATION_TERMINAL_REASONS)[number];

const TERMINAL_REASON_SET: ReadonlySet<string> = new Set(IMPERSONATION_TERMINAL_REASONS);

/** Whether a refusal code is one of the closed terminal codes. */
export function isImpersonationTerminalReason(code: unknown): code is ImpersonationTerminalReason {
  return typeof code === "string" && TERMINAL_REASON_SET.has(code);
}

/**
 * The terminal reason carried by a failed response, or null when the response
 * is not an impersonation end. The middleware answers 403; 401 is accepted as
 * well so a future move of the refusal to 401 cannot silently reopen the
 * refresh-and-retry path this branch exists to close.
 */
export function readImpersonationTerminalReason(
  error: Pick<AxiosError, "response"> | null | undefined,
): ImpersonationTerminalReason | null {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return null;
  const data = error?.response?.data as { details?: { reason?: unknown } } | undefined;
  const reason = data?.details?.reason;
  return isImpersonationTerminalReason(reason) ? reason : null;
}

/**
 * The banner's end code for each terminal reason - EXACTLY ONE message per
 * code. `undefined` renders the card's generic "The view has ended.", which is
 * the honest answer where the wire does not say which end it was.
 */
export const TERMINAL_REASON_END_CODE: Readonly<
  Record<ImpersonationTerminalReason, ImpersonationEndCode | undefined>
> = {
  impersonation_binding_ended: undefined,
  impersonation_lifecycle_ended: undefined,
  impersonation_reauth: "imp_reauth",
  act_as_binding_mismatch: undefined,
  impersonation_disabled: "imp_kill_switch",
  impersonation_refused_sovereign_mode: undefined,
};

/** The window event the interceptor dispatches on a terminal refusal. */
export const IMPERSONATION_TERMINAL_EVENT = "impersonation:terminal";

/** The detail of {@link IMPERSONATION_TERMINAL_EVENT}. */
export interface ImpersonationTerminalEventDetail {
  reason: ImpersonationTerminalReason;
}

// ---------------------------------------------------------------------------
// The shell's layout signal (a mirror of AuthContext, never a second owner)
// ---------------------------------------------------------------------------
//
// AuthContext OWNS the impersonation state. Two facts about it are needed by
// modules that must not depend on AuthContext being mounted - the shared
// layouts' banner offset and the permission hook's re-check cue - so the shell
// PUBLISHES them here and those modules subscribe. Nothing here is persisted,
// and nothing here is an input to any authorization or header decision: the
// header reads the act-as getter above.

/** What the shell publishes. */
export interface ImpersonationShellSignal {
  /** Whether the fixed banner strip (or the failed-exit notice) is shown. */
  bannerShown: boolean;
  /** Bumped when the re-check poll reports a stale server-side role re-check. */
  permissionsEpoch: number;
}

const EMPTY_SIGNAL: ImpersonationShellSignal = { bannerShown: false, permissionsEpoch: 0 };
let _signal: ImpersonationShellSignal = EMPTY_SIGNAL;
const _listeners = new Set<() => void>();

/** Publish the shell's signal (AuthContext only). */
export function publishImpersonationShellSignal(next: ImpersonationShellSignal): void {
  if (
    next.bannerShown === _signal.bannerShown &&
    next.permissionsEpoch === _signal.permissionsEpoch
  ) {
    return;
  }
  _signal = next;
  _listeners.forEach((listener) => listener());
}

function subscribeShellSignal(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

function readShellSignal(): ImpersonationShellSignal {
  return _signal;
}

/** Subscribe to the shell's signal. Safe with no AuthProvider mounted. */
export function useImpersonationShellSignal(): ImpersonationShellSignal {
  return useSyncExternalStore(subscribeShellSignal, readShellSignal, readShellSignal);
}

// ---------------------------------------------------------------------------
// Shell helpers
// ---------------------------------------------------------------------------

/**
 * The identity key the three SPA mounts put on the wrapper that CONTAINS
 * PermissionProvider (RULE-095(2)): `${target_user_id}:${session_ref}` while a
 * binding is live, a constant otherwise. It changes exactly twice per binding
 * (entry and exit) and never on a token refresh.
 */
export function impersonationIdentityKey(descriptor: ImpersonationDescriptor | null): string {
  return descriptor ? `${descriptor.target.user_id}:${descriptor.session_ref}` : "self";
}

/**
 * The banner's props shape from the wire descriptor.
 *
 * THE DESCRIPTOR CARRIES NO NAME, EMAIL OR TENANT NAME (dto.go: omitted for
 * the one-indexed-read performance contract), while the banner's props require
 * all three. The fallbacks are the identifiers the descriptor DOES carry, so
 * the strip always names somebody; the email is empty because nothing better
 * exists without a second read.
 */
export function toBannerBinding(descriptor: ImpersonationDescriptor): ImpersonationBinding {
  return {
    session_ref: descriptor.session_ref,
    mode: descriptor.mode,
    depth: descriptor.depth,
    target: {
      display_name: descriptor.target.display_name || descriptor.target.user_id,
      email: "",
      tenant_name: descriptor.target.tenant_name || descriptor.target.tenant_id,
    },
    via: descriptor.via
      ? { display_name: descriptor.via.display_name || descriptor.via.user_id }
      : undefined,
    expires_at: descriptor.expires_at,
  };
}

/**
 * The return-to URL if it is a navigable http(s) URL, else null. The server
 * validated it against the caller's own Origin at start; this refuses anything
 * else (a `javascript:` URL above all) rather than navigating to it.
 */
export function safeReturnTo(returnTo: string | undefined): string | null {
  if (!returnTo) return null;
  try {
    const url = new URL(returnTo, window.location.origin);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The admin-side calls (TASK-IMP-ADMIN-ENTRY-CHG-008): the start, the
// platform-scoped list and the admin revoke. They live HERE, in the same owning
// module, so no second module ever declares these keys (RULE-077,
// RULE-095(11)).
// ---------------------------------------------------------------------------

/** The two act-as modes. `view` is read-only; `act` may change data. */
export type ImpersonationMode = "view" | "act";

/**
 * The server's minimum reason length in characters (CONTRACT-038;
 * `eligibility.MinReasonLength`). The dialog validates against it on blur so
 * the refusal is explained BEFORE the round trip, never only after it.
 */
export const IMPERSONATION_MIN_REASON_LENGTH = 5;

/**
 * The server's maximum reason length in characters (`MaxReasonLength` in
 * `internal/api/impersonation/handler.go`). A longer reason is refused 400
 * `impersonation_reason_too_long`, never truncated, so the field refuses it
 * too rather than letting the operator type a sentence the server will reject.
 */
export const IMPERSONATION_MAX_REASON_LENGTH = 1000;

/** The body of `POST /api/v1/impersonation/sessions` (dto.go `StartRequest`). */
export interface StartImpersonationRequest {
  /** The identity-provider subject (a UUID) of the person to view as. */
  target_user_id: string;
  mode: ImpersonationMode;
  /** REQUIRED, between the minimum and the maximum length above. */
  reason: string;
  /**
   * Where the terminal card sends the operator back to. The server validates
   * it against the request's own Origin, so it must be on THIS origin.
   */
  return_to: string;
}

/**
 * The 201 body of the start (dto.go `StartResponse`, CONTRACT-038).
 *
 * IT CARRIES NO SECRET OF ANY KIND, because none exists: the session_ref is
 * non-secret and inert for anyone but the bound impersonator. There is no
 * token field here and none may be added (critic cut C3).
 */
export interface ImpersonationStartResponse {
  session_ref: string;
  mode: ImpersonationMode;
  depth: number;
  parent_session_ref?: string;
  via?: ImpersonationPersonRef;
  /** On the 201 the target block DOES carry display_name and tenant_name. */
  target: ImpersonationTargetBlock;
  target_surface: "tenant" | "workspace";
  /** The cross-origin landing URL (`<target SPA origin>/?imp=<session_ref>`). */
  landing_url: string;
  expires_at: string;
  return_to: string;
}

/**
 * EVERY typed refusal code `POST /api/v1/impersonation/sessions` can answer
 * with, MEASURED FROM THE CODE rather than copied from the TASK text: the
 * route's own codes (`internal/api/impersonation/handler.go`), the eligibility
 * ladder's (`internal/impersonation/eligibility/ladder.go`) and the act-as
 * middleware's (`internal/api/middleware/actas.go`, `actas_refusal.go`). The
 * dialog maps each one to its own translated sentence; a code outside this
 * list renders the generic fallback naming the code.
 *
 * `impersonation_action_refused` is the one a NESTED start actually receives
 * from inside a view binding or a depth-2 binding: the act-as refusal layer
 * answers it before the ladder is reached, so the ladder's
 * `impersonation_view_cannot_start` / `impersonation_depth_exceeded` are
 * defence in depth that the wire normally never shows.
 */
export const IMPERSONATION_START_REFUSAL_CODES = [
  // 400
  "impersonation_body_invalid",
  "impersonation_reason_too_long",
  "impersonation_reason_too_short",
  "impersonation_target_invalid",
  "impersonation_return_to_invalid",
  "impersonation_mode_invalid",
  "impersonation_self",
  // 401
  "impersonation_principal_not_user",
  // 403
  "impersonation_rank_not_below",
  "impersonation_rank_unrankable",
  "impersonation_role_carveout_target",
  "impersonator_tenant_unbound",
  "target_tenant_unbound",
  "target_tenant_suspended",
  "target_tenant_unavailable",
  "target_tenant_consent_off",
  "target_disabled",
  "impersonation_target_exceeds_caller",
  "impersonation_act_not_permitted",
  "impersonation_view_cannot_start",
  "impersonation_depth_exceeded",
  "impersonation_action_refused",
  "impersonation_settings_unreadable",
  "impersonation_disabled",
  "impersonation_refused_sovereign_mode",
  "impersonation_refused_bypass_posture",
  // 404
  "target_not_found",
  // 409
  "platform_admin_not_impersonatable",
  "impersonation_already_active",
  // 503
  "impersonation_identity_unavailable",
  "impersonation_resolver_unavailable",
  "impersonation_carrier_unconfigured",
  "impersonation_landing_unconfigured",
  "impersonation_binding_store_unreadable",
  "impersonation_tenant_consent_unreadable",
] as const;

export type ImpersonationStartRefusalCode = (typeof IMPERSONATION_START_REFUSAL_CODES)[number];

const START_REFUSAL_SET: ReadonlySet<string> = new Set(IMPERSONATION_START_REFUSAL_CODES);

/** Whether a code is one of the start route's typed refusals. */
export function isImpersonationStartRefusalCode(code: unknown): code is ImpersonationStartRefusalCode {
  return typeof code === "string" && START_REFUSAL_SET.has(code);
}

/**
 * A refused start, read from the house error body
 * `{error, code, message, details: {reason, session_ref?, offending_permissions?}}`.
 */
export class ImpersonationStartError extends Error {
  /** The HTTP status, or 0 when no response arrived (a network failure). */
  readonly status: number;
  /** The typed code from `details.reason`, or null when the body carried none. */
  readonly code: string | null;
  /** The LIVE binding a 409 `impersonation_already_active` names. */
  readonly liveSessionRef: string | null;
  /** The permissions a 403 `impersonation_target_exceeds_caller` names. */
  readonly offendingPermissions: string[];

  constructor(status: number, code: string | null, message: string, liveSessionRef: string | null, offendingPermissions: string[]) {
    super(message);
    this.name = "ImpersonationStartError";
    this.status = status;
    this.code = code;
    this.liveSessionRef = liveSessionRef;
    this.offendingPermissions = offendingPermissions;
  }
}

/** Build the typed refusal from whatever the api client rejected with. */
export function toImpersonationStartError(err: unknown): ImpersonationStartError {
  if (err instanceof ImpersonationStartError) return err;
  const response = (err as AxiosError | undefined)?.response;
  const data = response?.data as
    | { message?: unknown; details?: { reason?: unknown; session_ref?: unknown; offending_permissions?: unknown } }
    | undefined;
  const details = data?.details;
  const code = typeof details?.reason === "string" && details.reason ? details.reason : null;
  const live = typeof details?.session_ref === "string" && details.session_ref ? details.session_ref : null;
  const offending = Array.isArray(details?.offending_permissions)
    ? details.offending_permissions.filter((p): p is string => typeof p === "string")
    : [];
  const message =
    typeof data?.message === "string" && data.message
      ? data.message
      : (err as Error | undefined)?.message ?? "impersonation start failed";
  return new ImpersonationStartError(response?.status ?? 0, code, message, live, offending);
}

/**
 * Start an act-as binding (SPEC-GO-API-CONTRACT-038).
 *
 * The request is stamped like any other: from the admin origin no binding is
 * armed, so no act-as header rides along; from inside an act ROOT binding the
 * header is present and the server evaluates a NESTED start. Every refusal
 * rejects with an {@link ImpersonationStartError} carrying the typed code - the
 * client never decides eligibility, it renders the server's verdict.
 */
export async function startImpersonationSession(
  request: StartImpersonationRequest,
): Promise<ImpersonationStartResponse> {
  try {
    const res = await client.post<ImpersonationStartResponse>("/impersonation/sessions", request);
    return res.data;
  } catch (err) {
    throw toImpersonationStartError(err);
  }
}

/** One party of an admin-list row (dto.go `AdminPartyRef`). */
export interface AdminImpersonationPartyRef {
  user_id: string;
  tenant_id?: string;
}

/** One row of CONTRACT-040 (dto.go `AdminSessionRow`). */
export interface AdminImpersonationSessionRow {
  session_ref: string;
  impersonator: AdminImpersonationPartyRef;
  target: ImpersonationTargetBlock;
  mode: ImpersonationMode;
  depth: number;
  via?: ImpersonationPersonRef;
  reason: string;
  started_at: string;
  expires_at: string;
  last_seen_at: string | null;
  ended_at: string | null;
  end_reason: string;
}

/** The 200 body of CONTRACT-040 (dto.go `AdminListResponse`). */
export interface AdminImpersonationSessionList {
  sessions: AdminImpersonationSessionRow[];
  total: number;
}

/**
 * THE cache key for the platform-scoped list of LIVE bindings. Paired with
 * {@link fetchAdminImpersonationSessions}; a second queryFn on it fails
 * QUERY-KEY-GATE. The active-view strip does NOT read it - it reads the
 * caller's own descriptor - so its only consumer is the admin sessions view.
 */
export const ADMIN_IMPERSONATION_SESSIONS_QUERY_KEY = ["admin", "impersonation", "sessions"] as const;

/**
 * Read the platform-scoped list of LIVE bindings (CONTRACT-040). The backend
 * answers `?active=false` with 400 `impersonation_inactive_list_unsupported`
 * (the store exposes no read of ended rows yet), so only the live list is
 * offered here.
 */
export async function fetchAdminImpersonationSessions(): Promise<AdminImpersonationSessionList> {
  const res = await client.get<AdminImpersonationSessionList>("/admin/impersonation/sessions");
  const body = res.data;
  return {
    sessions: Array.isArray(body?.sessions) ? body.sessions : [],
    total: typeof body?.total === "number" ? body.total : 0,
  };
}

/** The query options for the admin list: the key and the fetcher travel together. */
export const adminImpersonationSessionsQuery = {
  queryKey: ADMIN_IMPERSONATION_SESSIONS_QUERY_KEY,
  queryFn: fetchAdminImpersonationSessions,
};

/**
 * One tenant's view-as consent (SPEC-GO-API-CONTRACT-064, and the 200 body of
 * CONTRACT-065; dto.go `TenantConsentResponse`). OFF is the default, and a
 * tenant with no stored row reads OFF with both instants null.
 * `live_bindings_ended` rides only the PUT's answer: how many live bindings
 * into the tenant a withdrawal ended at once, `null` when no eager end ran
 * (an ON write, or the eager-end seam not wired - every governed binding still
 * ends on its next request).
 */
export interface TenantImpersonationConsent {
  tenant_id: string;
  impersonation_consent: boolean;
  changed_at: string | null;
  reason: string;
  withdrawn_at: string | null;
  live_bindings_ended?: number | null;
}

/** The PUT body of CONTRACT-065: both fields, the reason exactly as typed. */
export interface TenantImpersonationConsentWrite {
  impersonation_consent: boolean;
  reason: string;
}

/**
 * THE cache key for one tenant's view-as consent. Paired with
 * {@link fetchTenantImpersonationConsent}; a second queryFn on it fails
 * QUERY-KEY-GATE (SPEC-FRONTEND-RULE-077).
 */
export const TENANT_IMPERSONATION_CONSENT_QUERY_KEY = (tenantId: string) =>
  ["admin", "impersonation", "tenant-consent", tenantId] as const;

/**
 * Read one tenant's view-as consent (CONTRACT-064, platform administrators
 * only). A failed read rejects - it is never answered as OFF, which would
 * invite granting a consent already granted or withdrawing one believed absent.
 */
export async function fetchTenantImpersonationConsent(tenantId: string): Promise<TenantImpersonationConsent> {
  const res = await client.get<TenantImpersonationConsent>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/impersonation-consent`,
  );
  return res.data;
}

/**
 * The CALLER's own tenant's view-as consent, read-only (CONTRACT-068; F13 legal
 * review K2, D-IMP-56): whether it is on, when it last changed, and the
 * contract reference the platform administrator recorded. The tenant is the
 * caller's own; nothing in the request names it.
 */
export interface OwnTenantImpersonationConsent {
  enabled: boolean;
  changed_at: string | null;
  contract_reference: string;
}

/**
 * THE cache key for the caller's own tenant's view-as consent. Paired with
 * {@link fetchOwnTenantImpersonationConsent}; a second queryFn on it fails
 * QUERY-KEY-GATE (SPEC-FRONTEND-RULE-077).
 */
export const OWN_TENANT_IMPERSONATION_CONSENT_QUERY_KEY = ["tenant", "impersonation", "own-consent"] as const;

/**
 * Read the caller's own tenant's view-as consent (CONTRACT-068, audit.view). A
 * failed read rejects - it is never answered as OFF, which would tell the
 * controller its processor cannot view its users when that is not known.
 */
export async function fetchOwnTenantImpersonationConsent(): Promise<OwnTenantImpersonationConsent> {
  const res = await client.get<OwnTenantImpersonationConsent>("/tenant/impersonation-consent");
  return res.data;
}

/**
 * Write one tenant's view-as consent (CONTRACT-065). The body is exactly
 * `{impersonation_consent, reason}` with the reason UNTRIMMED: the server
 * trims and judges it (SPEC-FRONTEND-RULE-097 (3)), and a refusal rejects with
 * the house error body for the caller to show.
 */
export async function putTenantImpersonationConsent(
  tenantId: string,
  write: TenantImpersonationConsentWrite,
): Promise<TenantImpersonationConsent> {
  const res = await client.put<TenantImpersonationConsent>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/impersonation-consent`,
    { impersonation_consent: write.impersonation_consent, reason: write.reason },
  );
  return res.data;
}

/** How an admin revoke ended. */
export type ImpersonationRevokeOutcome = "ended" | "not_found";

/**
 * The PLATFORM-ADMIN revoke `DELETE /api/v1/admin/impersonation/sessions/{ref}`
 * (CONTRACT-041). A DIFFERENT call from {@link exitImpersonationSession}: it
 * needs impersonation.admin and a platform-admin identity, so the active-view
 * strip never uses it - a tenant admin could not. 404 (unknown or already
 * ended) resolves as `not_found`; anything else rejects.
 */
export async function revokeImpersonationSession(sessionRef: string): Promise<ImpersonationRevokeOutcome> {
  try {
    await client.delete(`/admin/impersonation/sessions/${encodeURIComponent(sessionRef)}`);
    return "ended";
  } catch (err) {
    if ((err as AxiosError | undefined)?.response?.status === 404) {
      return "not_found";
    }
    throw err;
  }
}

/**
 * Whether a LIVE binding reference is the caller's OWN, decided by ASKING THE
 * SERVER rather than inferred from the row the operator started from: the
 * caller's own descriptor names the innermost live binding and its parent, so
 * a 409's session_ref that matches either is the caller's own and takes the
 * self-exit; anything else takes the admin revoke.
 */
export function isOwnLiveBinding(
  descriptor: ImpersonationDescriptor | null,
  sessionRef: string,
): boolean {
  if (!descriptor || !sessionRef) return false;
  return descriptor.session_ref === sessionRef || descriptor.parent_session_ref === sessionRef;
}
